import * as React from "react";
import { useState } from "react";
import { useMutation } from "@apollo/client";
import { SIGN_UP_MUTATION, SIGN_IN_MUTATION } from "@/graphql/mutations";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Logo from "@/components/views/Logo";
import GoogleOAuthButton from "@/components/oauth/GoogleOAuthButton";
import InstagramOAuthButton from "@/components/oauth/InstagramOAuthButton";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useAuth } from "@/components/hooks/useAuth";

function RegisterComponent() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [registrationError, setRegistrationError] = useState<string | null>(
    null
  );
  const navigate = useNavigate();
  const { login, setUser } = useAuth();

  // Perform sign-in immediately after successful sign-up
  const [signIn, { loading: signingIn }] = useMutation(SIGN_IN_MUTATION, {
    onCompleted: (data) => {
      const resp = data?.signIn;
      if (resp?.token) {
        // Set user snapshot then trigger auth flow redirect
        setUser(resp.user);
        login(resp.token);
        toast.success("Welcome — your atelier awaits.");
      } else if (resp?.mfaRequired) {
        toast.success("Account created. Please complete MFA to continue.");
        navigate("/login");
      } else {
        toast.error("Automatic sign-in failed. Please log in.");
        navigate("/login");
      }
    },
    onError: () => {
      toast.error("Automatic sign-in failed. Please log in.");
      navigate("/login");
    },
  });

  const [signUp, { loading }] = useMutation(SIGN_UP_MUTATION, {
    onCompleted: (data) => {
      console.log("Registration completed:", data);
      const { success, errors } = data.signUp;
      if (success) {
        // Auto sign-in then let useAuth.login handle redirects/onboarding
        toast.success("Registration successful — signing you in...");
        signIn({ variables: { email, password, mfaRememberToken: null } });
      } else {
        console.error("Registration failed:", data);
        // Display error messages from the backend
        if (errors && errors.fullMessages && errors.fullMessages.length > 0) {
          // Show each error message as a separate toast
          errors.fullMessages.forEach((message: string) => {
            toast.error(message);
          });
        } else {
          toast.error("Registration failed. Please check your details.");
        }
      }
    },
    onError: (error) => {
      console.error("Registration failed:", error);
      setRegistrationError(error.message);
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setRegistrationError(null);
    signUp({
      variables: { userArgs: { firstName, lastName, email, password } },
    });
  };

  return (
    <>
      {/* Elegant background with subtle texture */}
      <div className="relative overflow-hidden pb-10">
        {/* Gradient background */}
        <div className="relative z-10 flex flex-1 flex-col justify-center">
          <div className="sm:mx-auto sm:w-full sm:max-w-md justify-center flex flex-col items-center">
            <Logo />

            {/* Elegant header */}
            <div className="text-center mt-6 mb-1">
              <h2 className="text-2xl md:text-3xl font-thin leading-tight text-gray-900 dark:text-white tracking-wide">
                <span className="italic font-extralight bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                  Create Account
                </span>
              </h2>
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent mx-auto mt-4" />
            </div>
          </div>

          {/* Floating gradient orbs */}
          <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-gradient-to-r from-purple-300/20 to-pink-300/20 dark:from-purple-600/10 dark:to-pink-600/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-r from-blue-300/20 to-indigo-300/20 dark:from-blue-600/10 dark:to-indigo-600/10 rounded-full blur-3xl animate-pulse delay-1000" />

          <div className="sm:mx-auto sm:w-full sm:max-w-[480px]">
            {/* Main form card */}
            <div className="mt-4 sm:mx-auto sm:w-full sm:max-w-md">
              <div className="relative">
                {/* Glass morphism card */}
                <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30 px-8 py-8 shadow-2xl rounded-sm">
                  {/* Subtle inner glow */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/10 to-transparent dark:from-gray-700/10 pointer-events-none" />

                  <div className="relative z-10">
                    {/* Error display */}
                    {registrationError && (
                      <div className="mb-6 p-4 rounded-xl bg-red-50/80 dark:bg-red-900/20 border border-red-200/50 dark:border-red-800/50 backdrop-blur-sm">
                        <p className="text-sm font-light text-red-600 dark:text-red-400 tracking-wide">
                          {registrationError}
                        </p>
                      </div>
                    )}

                    <form className="space-y-6" onSubmit={handleSubmit}>
                      {/* Name fields in a grid */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* First Name field */}
                        <div>
                          <label
                            htmlFor="firstName"
                            className="block text-sm font-light tracking-wider uppercase text-gray-600 dark:text-gray-400 mb-2"
                          >
                            First Name
                          </label>
                          <input
                            id="firstName"
                            name="firstName"
                            type="text"
                            autoComplete="given-name"
                            required
                            className="block w-full px-4 py-3 text-gray-900 dark:text-gray-100 bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-600 rounded-sm backdrop-blur-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500 focus:border-transparent transition-all duration-300 font-light tracking-wide"
                            placeholder="First name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                          />
                          <div className="absolute bottom-0 left-0 w-0 group-focus-within:w-full h-px bg-gradient-to-r from-gray-400 to-gray-600 dark:from-gray-500 dark:to-gray-400 transition-all duration-500" />
                        </div>
                        {/* Last Name field */}
                        <div className="group">
                          <label
                            htmlFor="lastName"
                            className="block text-xs font-light tracking-[0.15em] uppercase text-gray-700 dark:text-gray-300 mb-3"
                          >
                            Last Name
                          </label>
                          <div className="relative">
                            <input
                              id="lastName"
                              name="lastName"
                              type="text"
                              autoComplete="family-name"
                              required
                              className="block w-full px-4 py-3 text-gray-900 dark:text-gray-100 bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-600 rounded-sm backdrop-blur-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500 focus:border-transparent transition-all duration-300 font-light tracking-wide"
                              placeholder="Last name"
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                            />
                            <div className="absolute bottom-0 left-0 w-0 group-focus-within:w-full h-px bg-gradient-to-r from-gray-400 to-gray-600 dark:from-gray-500 dark:to-gray-400 transition-all duration-500" />
                          </div>
                        </div>
                      </div>

                      {/* Email field */}
                      <div className="group">
                        <label
                          htmlFor="email"
                          className="block text-xs font-light tracking-[0.15em] uppercase text-gray-700 dark:text-gray-300 mb-3"
                        >
                          Email Address
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          className="block w-full px-4 py-3 text-gray-900 dark:text-gray-100 bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-600 rounded-sm backdrop-blur-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500 focus:border-transparent transition-all duration-300 font-light tracking-wide"
                          placeholder="Enter your email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                        <div className="absolute bottom-0 left-0 w-0 group-focus-within:w-full h-px bg-gradient-to-r from-gray-400 to-gray-600 dark:from-gray-500 dark:to-gray-400 transition-all duration-500" />
                      </div>

                      {/* Password field */}
                      <div>
                        <label
                          htmlFor="password"
                          className="block text-xs font-light tracking-[0.15em] uppercase text-gray-700 dark:text-gray-300 mb-3"
                        >
                          Password
                        </label>
                        <input
                          id="password"
                          name="password"
                          type="password"
                          autoComplete="new-password"
                          required
                          className="block w-full px-4 py-3 text-gray-900 dark:text-gray-100 bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-600 rounded-sm backdrop-blur-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500 focus:border-transparent transition-all duration-300 font-light tracking-wide"
                          placeholder="Create a password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        <div className="absolute bottom-0 left-0 w-0 group-focus-within:w-full h-px bg-gradient-to-r from-gray-400 to-gray-600 dark:from-gray-500 dark:to-gray-400 transition-all duration-500" />
                      </div>

                      {/* Submit button */}
                      <div className="pt-2">
                        <button
                          type="submit"
                          disabled={loading || signingIn}
                          className="group relative w-full px-8 py-3 text-sm font-light tracking-wider uppercase text-white bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 dark:from-gray-200 dark:via-gray-100 dark:to-gray-200 dark:text-gray-900 rounded-sm border border-gray-600 dark:border-gray-300 hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                        >
                          <span className="relative z-10">
                            {loading || signingIn ? "Creating Account..." : "Create Account"}
                          </span>
                          {!loading && (
                            <div className="absolute inset-0 bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700 dark:from-gray-100 dark:via-white dark:to-gray-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-sm" />
                          )}
                        </button>
                      </div>
                    </form>

                    {/* Elegant divider */}
                    <div className="mt-8">
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-200/60 dark:border-gray-600/40" />
                        </div>
                        <div className="relative flex justify-center">
                          <span className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm px-6 text-xs font-light tracking-[0.2em] uppercase text-gray-500 dark:text-gray-400">
                            Or continue with
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 grid grid-cols-2 gap-4">
                      <div>
                        {(import.meta as any).env.VITE_GOOGLE_CLIENT_ID ? (
                          <GoogleOAuthButton
                            onSuccess={() => {
                              // Success is handled in the component
                            }}
                            onError={(error) => {
                              setRegistrationError(error);
                            }}
                          />
                        ) : (
                          <button
                            disabled
                            className="flex items-center justify-center gap-3 w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-sm font-medium text-gray-400 cursor-not-allowed"
                          >
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                              <path
                                fill="#9CA3AF"
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                              />
                              <path
                                fill="#9CA3AF"
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                              />
                              <path
                                fill="#9CA3AF"
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                              />
                              <path
                                fill="#9CA3AF"
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                              />
                            </svg>
                            Google (Not Configured)
                          </button>
                        )}
                      </div>

                      <div>
                        <InstagramOAuthButton
                          onSuccess={() => {
                            // Success is handled in the component
                          }}
                          onError={(error) => {
                            setRegistrationError(error);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Elegant footer link */}
              <div className="mt-8 text-center">
                <p className="text-sm font-light text-gray-500 dark:text-gray-400 tracking-wide">
                  Already have an account?{" "}
                  <a
                    href="/login"
                    className="group relative font-light tracking-wider text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-all duration-300"
                  >
                    <span className="relative z-10">Sign In</span>
                    <div className="absolute inset-x-0 -bottom-1 h-px bg-gradient-to-r from-transparent via-gray-400 dark:via-gray-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function Register() {
  const googleClientId = (import.meta as any).env.VITE_GOOGLE_CLIENT_ID;

  // If Google Client ID is not configured, render without Google OAuth provider
  if (!googleClientId) {
    console.warn(
      "VITE_GOOGLE_CLIENT_ID environment variable is not set. Google OAuth will be disabled."
    );
    return <RegisterComponent />;
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <RegisterComponent />
    </GoogleOAuthProvider>
  );
}
